import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, CornerDownLeft } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Skeleton } from "../ui/skeleton";
import { TICKET_PAGE_SIZE, pageIndexFor } from "../../lib/basar";
import { useLang } from "../../context/LanguageContext";

// Celle-klassar per tilstand (design_guidelines → Basar module addition §2). Ingen transition:all.
const CELL_BASE = "relative inline-flex items-center justify-center rounded-md border text-xs sm:text-[13px] font-medium tabular-nums transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] select-none";
const CELL_STATE = {
  available: "border-border bg-card text-foreground hover:bg-accent active:scale-[0.98] cursor-pointer",
  selected: "border-[hsl(var(--brand))]/50 bg-[hsl(var(--brand-soft))] text-foreground ring-1 ring-[hsl(var(--brand))]/40 active:scale-[0.98] cursor-pointer",
  held: "border-[hsl(var(--info))]/30 bg-[hsl(var(--info))]/12 text-foreground/70 cursor-not-allowed",
  sold: "border-border bg-[hsl(var(--muted))] text-muted-foreground line-through cursor-not-allowed",
  winner: "border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/18 text-foreground ring-2 ring-[hsl(var(--warning))]/40 cursor-default",
};

export const LEGEND_DOT = {
  available: "bg-card border border-border",
  selected: "bg-[hsl(var(--brand-soft))] border border-[hsl(var(--brand))]/50",
  held: "bg-[hsl(var(--info))]/30 border border-[hsl(var(--info))]/40",
  sold: "bg-[hsl(var(--muted))] border border-border",
  winner: "bg-[hsl(var(--warning))]/40 border border-[hsl(var(--warning))]/60",
};

export const TicketGridSkeleton = ({ compact = false }) => (
  <div className="space-y-3" data-testid="basar-loading-state">
    <div className="flex flex-wrap gap-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-6 w-20 rounded-full" />)}</div>
    <div className={`grid gap-2 ${compact ? "grid-cols-8" : "grid-cols-6 sm:grid-cols-8 lg:grid-cols-10"}`}>
      {[...Array(compact ? 32 : 36)].map((_, i) => <Skeleton key={i} className={compact ? "h-9" : "h-10 sm:h-9 lg:h-8"} />)}
    </div>
  </div>
);

/**
 * Rutenett over loddnummer (serie frå–til, opptil 10 000) – paginert 100 per side med «Hopp til».
 * `sold`, `held`, `winners`, `selected` er Set med nummer. Berre ledige/valde kan klikkast (`onToggle(n)`).
 * `disabled` gjer heile rutenettet skrivebeskytta (t.d. etter trekning). `compact` = mindre celler (dialogar).
 */
export const TicketGrid = ({
  seriesFrom,
  seriesTo,
  sold,
  held,
  winners,
  selected,
  onToggle,
  disabled = false,
  compact = false,
  maxSelectable,
  testId = "basar-ticket-grid",
  asOf,
  onRefresh,
}) => {
  const { t } = useLang();
  const from = Number(seriesFrom || 1);
  const to = Number(seriesTo || 0);
  const total = Math.max(0, to - from + 1);
  const pages = Math.max(1, Math.ceil(total / TICKET_PAGE_SIZE));
  const [page, setPage] = useState(0);
  const [jump, setJump] = useState("");
  const [flash, setFlash] = useState(null);

  useEffect(() => {
    if (page > pages - 1) setPage(pages - 1);
  }, [pages, page]);

  useEffect(() => {
    if (flash == null) return undefined;
    const id = setTimeout(() => setFlash(null), 1600);
    return () => clearTimeout(id);
  }, [flash]);

  const start = from + page * TICKET_PAGE_SIZE;
  const end = Math.min(to, start + TICKET_PAGE_SIZE - 1);
  const numbers = useMemo(() => Array.from({ length: Math.max(0, end - start + 1) }, (_, i) => start + i), [start, end]);

  const stateOf = (n) => {
    if (winners?.has(n)) return "winner";
    if (sold?.has(n)) return "sold";
    if (held?.has(n)) return "held";
    if (selected?.has(n)) return "selected";
    return "available";
  };

  const limitReached = maxSelectable != null && (selected?.size || 0) >= maxSelectable;

  const goToNumber = (raw) => {
    const n = parseInt(String(raw).replace(/\D/g, ""), 10);
    if (!Number.isFinite(n) || n < from || n > to) return false;
    setPage(pageIndexFor(n, from));
    setFlash(n);
    return true;
  };

  const quickPages = useMemo(() => {
    if (pages <= 1) return [];
    const set = new Set(compact ? [0, page, pages - 1] : [0, Math.floor(pages / 2), pages - 1, page]);
    if (pages <= (compact ? 3 : 5)) for (let i = 0; i < pages; i += 1) set.add(i);
    return [...set].sort((a, b) => a - b);
  }, [pages, page, compact]);

  const pageLabel = (p) => {
    const s = from + p * TICKET_PAGE_SIZE;
    return `${s}–${Math.min(to, s + TICKET_PAGE_SIZE - 1)}`;
  };

  const legend = [{ key: "available", label: disabled ? t("Ikke solgt") : t("Ledig") }];
  if (!disabled) legend.push({ key: "selected", label: t("Valgt") });
  if (!disabled || held?.size) legend.push({ key: "held", label: t("Reservert") });
  legend.push({ key: "sold", label: t("Solgt") });
  if (winners?.size) legend.push({ key: "winner", label: t("Vinner") });

  return (
    <div className="space-y-3" data-testid={testId} data-page={page} data-total={total}>
      {/* Legend */}
      <div className="flex flex-wrap items-center gap-2" data-testid="basar-ticket-legend">
        {legend.map((l) => (
          <Badge key={l.key} variant="outline" className="gap-1.5 font-normal" data-testid={`basar-ticket-legend-${l.key}`}>
            <span className={`h-2.5 w-2.5 rounded-full ${LEGEND_DOT[l.key]}`} aria-hidden="true" />
            {l.label}
          </Badge>
        ))}
        {onRefresh && (
          <button type="button" onClick={onRefresh} className="ml-auto text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground transition-colors duration-150" data-testid="basar-ticket-refresh-button">
            {t("Oppdater")}
          </button>
        )}
      </div>

      {/* Område-navigasjon (for lange seriar) */}
      {pages > 1 && (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between" data-testid="basar-ticket-pagination">
          <div className="flex items-center gap-1.5 flex-wrap">
            <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} aria-label={t("Forrige side")} data-testid="basar-ticket-prev-button"><ChevronLeft className="w-4 h-4" /></Button>
            {quickPages.map((p) => (
              <Button key={p} type="button" variant={p === page ? "default" : "secondary"} size="sm" className="h-8 px-2.5 tabular-nums" onClick={() => setPage(p)} aria-current={p === page ? "page" : undefined} data-testid={`basar-ticket-page-${p}`}>{pageLabel(p)}</Button>
            ))}
            <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => setPage((p) => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1} aria-label={t("Neste side")} data-testid="basar-ticket-next-button"><ChevronRight className="w-4 h-4" /></Button>
          </div>
          <form
            className="flex items-center gap-1.5"
            onSubmit={(e) => {
              e.preventDefault();
              if (goToNumber(jump)) setJump("");
            }}
          >
            <Input value={jump} onChange={(e) => setJump(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" placeholder={t("Hopp til nr.")} className="h-8 w-28 text-sm" aria-label={t("Hopp til nummer")} data-testid="basar-ticket-jump-input" />
            <Button type="submit" variant="secondary" size="sm" className="h-8" disabled={!jump} data-testid="basar-ticket-jump-button"><CornerDownLeft className="w-3.5 h-3.5 mr-1" /> {t("Gå")}</Button>
          </form>
        </div>
      )}

      <p className="text-xs text-muted-foreground tabular-nums" data-testid="basar-ticket-range-label">
        {t("Viser {start}–{end} av {total} lodd", { start, end, total })}
        {asOf ? "" : ""}
      </p>

      {/* Sjølve rutenettet */}
      <div role="group" aria-label={t("Loddnummer")} className={`grid gap-2 ${compact ? "grid-cols-6 sm:grid-cols-8" : "grid-cols-6 sm:grid-cols-8 lg:grid-cols-10"}`} data-testid="basar-ticket-cells">
        {numbers.map((n) => {
          const state = stateOf(n);
          const clickable = !disabled && (state === "available" || state === "selected") && !(state === "available" && limitReached);
          const stateLabel = { available: t("ledig"), selected: t("valgt"), held: t("reservert"), sold: t("solgt"), winner: t("vinner") }[state];
          return (
            <button
              key={n}
              type="button"
              onClick={() => clickable && onToggle?.(n)}
              disabled={!clickable}
              aria-pressed={state === "selected"}
              aria-label={t("Lodd {n} – {state}", { n, state: stateLabel })}
              className={`${CELL_BASE} ${CELL_STATE[state]} ${compact ? "h-9" : "h-10 sm:h-9 lg:h-8"} ${flash === n ? "ring-2 ring-[hsl(var(--brand))] ring-offset-1" : ""} ${!clickable && state === "available" ? "opacity-60" : ""}`}
              data-testid={`basar-ticket-cell-${state}`}
              data-number={n}
            >
              {n}
            </button>
          );
        })}
      </div>
    </div>
  );
};
